/**
 * Avatar utility functions
 * Generates consistent, gender-neutral avatars using DiceBear API
 */

type AvatarStyle = 'bottts' | 'fun-emoji' | 'shapes' | 'identicon' | 'thumbs' | 'rings';

const DEFAULT_STYLE: AvatarStyle = 'bottts';

/**
 * Generate a DiceBear avatar URL based on a seed (email, username, or ID)
 * Same seed always generates the same avatar
 */
export function generateAvatarUrl(
  seed: string,
  style: AvatarStyle = DEFAULT_STYLE,
  size: number = 128
): string {
  const encodedSeed = encodeURIComponent(seed);
  return `https://api.dicebear.com/7.x/${style}/svg?seed=${encodedSeed}&size=${size}`;
}

/**
 * Get the appropriate avatar URL for a user
 * Returns custom avatar if set, otherwise generates a DiceBear avatar
 */
export function getUserAvatarUrl(
  avatarUrl: string | null | undefined,
  fallbackSeed: string,
  style: AvatarStyle = DEFAULT_STYLE,
  size: number = 128
): string {
  // If user has a custom avatar, use it
  if (avatarUrl && avatarUrl.trim() !== '') {
    return avatarUrl;
  }

  // Otherwise generate a DiceBear avatar
  return generateAvatarUrl(fallbackSeed, style, size);
}

/**
 * Get initials from a name or email
 */
export function getInitials(name?: string | null, email?: string | null): string {
  if (name && name.trim()) {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  }

  if (email) {
    return email[0].toUpperCase();
  }

  return '?';
}
